import React, { useState, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  FormGroup,
  Row,
  Col,
  Spinner,
} from "reactstrap"
import Select from "react-select"
import axios from "axios"
import apiURl from "url"
import toastr from "toastr"
import "toastr/build/toastr.min.css"

const ContactEditModal = ({
  isOpen,
  toggle,
  contact,
  onSave,
  loadCities = () => {},
  citiesData = [],
  receiverCountryOptions = [],
  type,
}) => {
  const [form, setForm] = useState(contact)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // add city
  const [addCityModalOpen, setAddCityModalOpen] = useState(false)
  const [newCityName, setNewCityName] = useState("")
  const [pendingCitySelect, setPendingCitySelect] = useState(null)

  const edit = (field, value) => {
    setForm({ ...form, [field]: value })
    setErrors({ ...errors, [field]: "" })
  }

  // ===========================
  // VALIDATE FORM
  // ===========================
  const validate = () => {
    const newErrors = {}

    if (!form.first_name?.trim())
      newErrors.first_name = "First name is required"

    if (!form.last_name?.trim()) newErrors.last_name = "Last name is required"

    if (!form.phone?.trim()) newErrors.phone = "Phone is required"

    if (type === "sender" && !form.cnic?.trim())
      newErrors.cnic = "CNIC is required"

    if (type === "receiver" && !form.zip?.trim())
      newErrors.zip = "ZIP Code is required"

    if (type === "receiver" && !form.country_name)
      newErrors.country_name = "Country is required"

    if (!form.city_name) newErrors.city_name = "City is required"

    if (!form.full_address?.trim())
      newErrors.full_address = "Address is required"

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // ===========================
  // SAVE
  // ===========================
  const saveChanges = async e => {
    e.preventDefault()
    if (!validate()) return

    setIsLoading(true)
    try {
      const res = await axios.post(
        `${apiURl}/international/contacts/update`,
        form
      )

      if (res.data.status === "success") {
        onSave(form)
        toggle()
      }
    } catch (err) {
      console.error("UPDATE FAILED", err)
    } finally {
      setIsLoading(false)
    }
  }

  // ===========================
  // RESET ON CONTACT CHANGE
  // ===========================
  useEffect(() => {
    if (contact) {
      setForm({
        id: contact.id,
        first_name: contact.first_name || "",
        last_name: contact.last_name || "",
        phone: contact.phone || "",
        phone2: contact.phone2 || "",
        email: contact.email || "",
        zip: contact.zip || "",
        cnic: contact.cnic || "",
        country_name: contact.country_name || "",
        city_name: contact.city_name || "",
        full_address: contact.full_address || contact.current_address || "",
      })
    }
  }, [contact])

  // =====================================
  // ADD NEW CITY
  // =====================================
  const handleAddCity = async () => {
    if (!newCityName.trim()) return

    let finalCountry =
      type === "sender" ? "Pakistan" : form.country_name || null

    if (!finalCountry) {
      toastr.error("Please select a country first.")
      return
    }

    try {
      await axios.post(`${apiURl}/international/cities-options/add`, {
        city_name: newCityName.trim(),
        country_name: finalCountry,
      })

      setPendingCitySelect(newCityName.trim())
      edit("country_name", finalCountry)

      setAddCityModalOpen(false)
      setNewCityName("")
      loadCities()

      toastr.success("City added successfully!")
    } catch (err) {
      console.error(err)
      toastr.error("Failed to add city")
    }
  }

  // AUTO-SELECT RECENTLY ADDED CITY
  useEffect(() => {
    if (pendingCitySelect && citiesData.length > 0) {
      const exists = citiesData.find(c => c.name === pendingCitySelect)

      if (exists) {
        edit("city_name", pendingCitySelect)
        setPendingCitySelect(null)
      }
    }
  }, [citiesData])

  // ================================================
  // ⭐ NEW REQUIRED LOGIC
  // 1. Reset city when country changes
  // 2. Disable city until country selected
  // ================================================
  useEffect(() => {
    if (!contact) return

    if (form.country_name !== contact.country_name) {
      edit("city_name", "")
    }
  }, [form.country_name])

  const filteredCities = form.country_name
    ? citiesData.filter(c => c.country_name === form.country_name)
    : []

  const isCityDisabled = !form.country_name

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Edit Contact</ModalHeader>

      <ModalBody>
        <Row>
          {/* FIRST NAME */}
          <Col md={6}>
            <FormGroup>
              <Label>
                First Name <span className="text-danger">*</span>
              </Label>
              <Input
                value={form.first_name}
                invalid={!!errors.first_name}
                onChange={e => edit("first_name", e.target.value)}
              />
              {errors.first_name && (
                <small className="text-danger">{errors.first_name}</small>
              )}
            </FormGroup>
          </Col>

          {/* LAST NAME */}
          <Col md={6}>
            <FormGroup>
              <Label>
                Last Name <span className="text-danger">*</span>
              </Label>
              <Input
                value={form.last_name}
                invalid={!!errors.last_name}
                onChange={e => edit("last_name", e.target.value)}
              />
              {errors.last_name && (
                <small className="text-danger">{errors.last_name}</small>
              )}
            </FormGroup>
          </Col>

          {/* PHONE */}
          <Col md={6}>
            <FormGroup>
              <Label>
                Phone <span className="text-danger">*</span>
              </Label>
              <Input
                value={form.phone}
                invalid={!!errors.phone}
                onChange={e => edit("phone", e.target.value)}
              />
              {errors.phone && (
                <small className="text-danger">{errors.phone}</small>
              )}
            </FormGroup>
          </Col>

          {/* ALT PHONE */}
          <Col md={6}>
            <FormGroup>
              <Label>Alternate Phone</Label>
              <Input
                value={form.phone2}
                onChange={e => edit("phone2", e.target.value)}
              />
            </FormGroup>
          </Col>

          {/* EMAIL */}
          <Col md={6}>
            <FormGroup>
              <Label>Email</Label>
              <Input
                value={form.email}
                onChange={e => edit("email", e.target.value)}
              />
            </FormGroup>
          </Col>

          {/* CNIC */}
          {type === "sender" && (
            <Col md={6}>
              <FormGroup>
                <Label>
                  CNIC <span className="text-danger">*</span>
                </Label>
                <Input
                  value={form.cnic}
                  invalid={!!errors.cnic}
                  onChange={e => edit("cnic", e.target.value)}
                />
                {errors.cnic && (
                  <small className="text-danger">{errors.cnic}</small>
                )}
              </FormGroup>
            </Col>
          )}

          {/* ZIP */}
          <Col md={6}>
            <FormGroup>
              <Label>
                ZIP Code{" "}
                {type === "receiver" && <span className="text-danger">*</span>}
              </Label>
              <Input
                value={form.zip}
                invalid={!!errors.zip}
                onChange={e => edit("zip", e.target.value)}
              />
              {errors.zip && (
                <small className="text-danger">{errors.zip}</small>
              )}
            </FormGroup>
          </Col>

          {/* COUNTRY (Receiver) */}
          {type === "receiver" && (
            <Col md={6}>
              <FormGroup>
                <Label>
                  Country <span className="text-danger">*</span>
                </Label>
                <Select
                  options={receiverCountryOptions}
                  value={receiverCountryOptions.find(
                    x => x.value === form.country_name
                  )}
                  className={errors.country_name ? "is-invalid-select" : ""}
                  onChange={opt => edit("country_name", opt.value)}
                />
                {errors.country_name && (
                  <small className="text-danger">{errors.country_name}</small>
                )}
              </FormGroup>
            </Col>
          )}

          {/* CITY */}
          <Col md={12}>
            <FormGroup>
              <Label>
                City <span className="text-danger">*</span>
              </Label>

              <Select
                isDisabled={isCityDisabled}
                placeholder={
                  isCityDisabled ? "Select country first" : "Select city"
                }
                options={filteredCities.map(c => ({
                  value: c.name,
                  label: c.name,
                }))}
                value={
                  form.city_name
                    ? { value: form.city_name, label: form.city_name }
                    : null
                }
                className={errors.city_name ? "is-invalid-select" : ""}
                onChange={opt => edit("city_name", opt.value)}
              />

              {errors.city_name && (
                <small className="text-danger">{errors.city_name}</small>
              )}

              <Button
                color="link"
                className="p-0 mt-1"
                disabled={isCityDisabled}
                onClick={() => setAddCityModalOpen(true)}
              >
                + Add New City
              </Button>
            </FormGroup>
          </Col>

          {/* ADDRESS */}
          <Col md={12}>
            <FormGroup>
              <Label>
                Full Address <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                rows="2"
                value={form.full_address}
                invalid={!!errors.full_address}
                onChange={e => edit("full_address", e.target.value)}
              />
              {errors.full_address && (
                <small className="text-danger">{errors.full_address}</small>
              )}
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter>
        <Button color="primary" onClick={saveChanges} disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner size="sm" /> Updating...
            </>
          ) : (
            "Save"
          )}
        </Button>

        <Button color="secondary" onClick={toggle} disabled={isLoading}>
          Cancel
        </Button>
      </ModalFooter>

      {/* ADD NEW CITY MODAL */}
      <Modal
        isOpen={addCityModalOpen}
        toggle={() => setAddCityModalOpen(false)}
      >
        <ModalHeader toggle={() => setAddCityModalOpen(false)}>
          Add New City
        </ModalHeader>

        <ModalBody>
          <Label className="fw-semibold">City Name</Label>
          <Input
            placeholder="Enter city name"
            value={newCityName}
            onChange={e => setNewCityName(e.target.value)}
            className="form-control sleek-input mb-3"
          />

          <Label className="fw-semibold">Country</Label>
          <Input
            disabled
            value={
              type === "sender"
                ? "Pakistan"
                : form.country_name || "No country selected"
            }
            className="form-control sleek-input mb-3"
          />

          <Button
            color="primary"
            className="w-100"
            onClick={handleAddCity}
            disabled={!newCityName.trim()}
          >
            Add City
          </Button>
        </ModalBody>
      </Modal>
    </Modal>
  )
}

export default ContactEditModal
